import { Link } from 'react-router-dom';
import { useApi } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { money, moneyShort, date, dateTime, titleCase } from '../lib/format.ts';
import { AgingBar, BarList, Card, Empty, ErrorBanner, Loading, MemberLink, Tile, Status } from '../components/ui.tsx';

interface Dashboard {
  aging: {
    total_minor: number;
    current_minor: number;
    days_30_minor: number;
    days_60_minor: number;
    days_90_plus_minor: number;
    account_count: number;
    accounts_needing_attention: number;
  };
  minimums: { open_count: number; at_risk_minor: number };
  open_period: { id: string; period_number: number; starts_on: string; ends_on: string } | null;
  health: {
    ledger_balances: boolean;
    out_of_balance_minor: number;
    entry_count: number;
    ar_reconciled: boolean;
    ar_variance_minor: number;
  };
  revenue_by_department: { department_code: string | null; department_name: string | null; revenue_minor: number }[];
  recent_activity: {
    id: string;
    txn_type: string;
    amount_minor: number;
    accounting_date: string;
    posted_at: string;
    description: string | null;
    member_account_id: string;
    member_name: string;
    account_number: string;
  }[];
}

export function DashboardPage() {
  const { club, currency } = useSession();
  const { data, error, loading } = useApi<Dashboard>('/v1/dashboard');

  if (loading) return <Loading rows={6} />;
  if (error) return <ErrorBanner error={error} />;
  if (!data) return null;

  const healthy = data.health.ledger_balances && data.health.ar_reconciled;

  return (
    <>
      <ErrorBanner error={error} />

      {/*
        Ledger health is the first thing on the screen and it is stated
        plainly. If either check fails, nothing else on this page can be
        trusted, so it is not buried in a report a controller has to
        remember to open.
      */}
      {!healthy && (
        <div className="error-banner">
          <strong>The books do not currently reconcile.</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {!data.health.ledger_balances && (
              <li>
                The trial balance is out by {money(data.health.out_of_balance_minor, currency)}.
              </li>
            )}
            {!data.health.ar_reconciled && (
              <li>
                The AR subledger differs from the control account by{' '}
                {money(data.health.ar_variance_minor, currency)}.
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="tiles">
        {/* Every one of these is a list that exists somewhere, and until
            now none of them said so — a controller read the number and
            then went looking for the screen behind it by hand. */}
        <Tile
          label="Member receivables"
          value={moneyShort(data.aging.total_minor, currency)}
          note={`${data.aging.account_count} accounts`}
          to="/members"
        />
        <Tile
          label="Over 90 days"
          value={moneyShort(data.aging.days_90_plus_minor, currency)}
          note={
            data.aging.days_90_plus_minor > 0
              ? 'needs a conversation'
              : 'nothing outstanding'
          }
          tone={data.aging.days_90_plus_minor > 0 ? 'critical' : 'good'}
          // Collections rather than the roster: the question behind this
          // number is who to ring, and that screen is the one with the
          // telephone numbers and the dunning history on it.
          to="/finance/collections"
        />
        <Tile
          label="Accounts not current"
          value={data.aging.accounts_needing_attention}
          note={data.aging.accounts_needing_attention > 0 ? 'past due or suspended' : 'all in good standing'}
          tone={data.aging.accounts_needing_attention > 0 ? 'warning' : 'good'}
          to="/members?status=past_due"
        />
        <Tile
          label="F&B minimums at risk"
          value={moneyShort(data.minimums.at_risk_minor, currency)}
          note={`${data.minimums.open_count} open periods`}
          to="/finance/statements"
        />
        <Tile
          label="Ledger"
          value={healthy ? 'Balanced' : 'Out'}
          note={`${data.health.entry_count} posted entries`}
          tone={healthy ? 'good' : 'critical'}
          to="/finance/trial-balance"
        />
      </div>

      <div className="grid-2">
        <Card title="Receivables by Age">
          <AgingBar
            currency={currency}
            buckets={[
              { label: 'Current', value: data.aging.current_minor, tone: 'good' },
              { label: '30 days', value: data.aging.days_30_minor, tone: 'warning' },
              { label: '60 days', value: data.aging.days_60_minor, tone: 'serious' },
              { label: '90+ days', value: data.aging.days_90_plus_minor, tone: 'critical' },
            ]}
          />
        </Card>

        <Card title="Revenue by Department">
          {data.revenue_by_department.length === 0 ? (
            <Empty>No revenue posted yet.</Empty>
          ) : (
            <BarList
              currency={currency}
              items={data.revenue_by_department.map((d) => ({
                label: d.department_name ?? d.department_code ?? 'Unassigned',
                value: Number(d.revenue_minor),
              }))}
            />
          )}
        </Card>
      </div>

      <Card
        title="Recent Activity"
        actions={<Link to="/members">All accounts →</Link>}
        flush
      >
        {data.recent_activity.length === 0 ? (
          <Empty>Nothing posted yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Date</th>
                <th>Member</th>
                <th>Type</th>
                <th>Description</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_activity.map((t) => (
                <tr key={t.id}>
                  {/* The posting time, where there is one. "When did
                      this actually go through" is the question a
                      disputed charge starts with, and a date alone
                      cannot answer it. */}
                  <td className="muted" title={t.posted_at ? dateTime(t.posted_at) : undefined}>
                    {date(t.accounting_date)}
                  </td>
                  <td>
                    {/* The most-looked-at list of member names in the
                        product, and the one place they were not
                        clickable: reading a charge and wanting to know
                        what else is on that account meant memorising the
                        name and going to search for it. */}
                    <MemberLink id={t.member_account_id} name={t.member_name} />{' '}
                    <span className="muted mono">{t.account_number}</span>
                  </td>
                  <td className="muted">{titleCase(t.txn_type)}</td>
                  <td>{t.description ?? '—'}</td>
                  <td className="num">
                    <span className={Number(t.amount_minor) < 0 ? 'neg' : undefined}>
                      {money(t.amount_minor, currency)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {data.open_period && (
        <div className="small muted">
          <Status tone="neutral">
            Open period {data.open_period.period_number}: {date(data.open_period.starts_on)} –{' '}
            {date(data.open_period.ends_on)} · {club?.brand_name}
          </Status>
        </div>
      )}
    </>
  );
}
